// Sales Brain V9 — "Less reading. More selling."
// Input form → 5-step pipeline → single-screen action dashboard.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { studioHeaders } from "@/lib/studioAuth";
import { MoreActions } from "@/components/MoreActions";
import { SalesChatPanel, GlobalSalesChat } from "@/pages/studio/SalesChat";
import { PortalAccess } from "@/pages/studio/PortalAccess";
import { LangCtx, useLang, tt, cx, li } from "@/pages/studio/sbLang";
import {
    Brain, Plus, Trash2, ArrowLeft, Upload, Loader2, RefreshCw, Copy, Share2,
    MessageCircle, FileText, Zap, ChevronDown, Flame, Phone, Star, Clapperboard,
    CheckCircle2, Circle, Sparkles, Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = {
    input: ["1/5 Input — fetching data", "1/5 输入 · 抓取资料"],
    analyze: ["2/5 Analyze — lean audit", "2/5 分析 · 极简审计"],
    proposal: ["3/5 Proposal — 2 pages + storyboard", "3/5 提案 · 2页提案+分镜"],
    price: ["4/5 Price — best package + quote", "4/5 报价 · 最佳配套"],
    close: ["5/5 Close — today's action", "5/5 成交 · 今日行动"],
    done: ["Done", "完成"],
};
const STEP_ORDER = ["input", "analyze", "proposal", "price", "close"];
const STAGES = [["new", "New lead", "新线索"], ["proposal_sent", "Proposal sent", "已发提案"],
["negotiating", "Negotiating", "谈判中"], ["won", "✅ Won", "✅ 成交"], ["lost", "❌ Lost", "❌ 流失"]];
const RATING_LABELS = [["brand", "Brand", "品牌"], ["visual", "Visual", "视觉"],
["content", "Content", "内容"], ["trust", "Trust", "信任"], ["cta", "CTA", "行动号召"]];

function copyText(text, label = "Copied · 已复制") {
    navigator.clipboard.writeText(text || "").then(() => toast.success(label)).catch(() => toast.error("Copy blocked · 复制被浏览器拦截"));
}
const rateTone = (v) => (v >= 4 ? "#5B7A3D" : v >= 3 ? "#B8863B" : "#B0503C");
const Stars = ({ v, size = 15 }) => (
    <span style={{ color: rateTone(v), fontSize: size, letterSpacing: 2 }}>{"★".repeat(v || 0)}{"☆".repeat(Math.max(0, 5 - (v || 0)))}</span>
);

// ---------- shell ----------
export function SalesBrainTab() {
    const [view, setView] = useState({ mode: "list" });
    const [lang, setLang] = useState(() => localStorage.getItem("sb_lang") || "cn");
    const changeLang = (l) => { setLang(l); localStorage.setItem("sb_lang", l); };
    return (
        <LangCtx.Provider value={lang}>
            <div className="flex justify-end mb-4">
                <div className="flex rounded-full border border-cafe-espresso/15 bg-white overflow-hidden" data-testid="sb-lang-switch">
                    {[["en", "English"], ["cn", "中文"], ["bi", "双语"]].map(([v, l]) => (
                        <button key={v} onClick={() => changeLang(v)} data-testid={`sb-lang-${v}`}
                            className={`px-4 py-1.5 text-[12px] transition-colors ${lang === v ? "bg-cafe-olive text-cafe-cream" : "text-cafe-mocha hover:text-cafe-espresso"}`}>
                            {l}
                        </button>
                    ))}
                </div>
            </div>
            {view.mode === "new" && <NewLeadForm onDone={(id) => setView(id ? { mode: "detail", id } : { mode: "list" })} />}
            {view.mode === "detail" && <LeadDetail id={view.id} onBack={() => setView({ mode: "list" })} />}
            {view.mode === "list" && <LeadList onOpen={(id) => setView({ mode: "detail", id })} onNew={() => setView({ mode: "new" })} />}
            <GlobalSalesChat />
        </LangCtx.Provider>
    );
}

// ---------- list ----------
function LeadList({ onOpen, onNew }) {
    const lang = useLang();
    const [rows, setRows] = useState(null);
    const load = async () => {
        try { const { data } = await axios.get(`${API}/sales-brain/leads`, { headers: studioHeaders() }); setRows(data || []); }
        catch { setRows([]); }
    };
    useEffect(() => { load(); }, []);
    const del = async (id) => {
        await axios.delete(`${API}/sales-brain/leads/${id}`, { headers: studioHeaders() });
        load();
    };
    return (
        <div data-testid="sales-brain-tab">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
                <div>
                    <div className="flex items-center gap-2 text-cafe-espresso">
                        <Brain className="h-5 w-5 text-cafe-olive" strokeWidth={1.75} />
                        <span className="font-serif text-2xl">Sales Brain V9</span>
                    </div>
                    <p className="text-sm text-cafe-mocha mt-1">{tt(lang, "Less reading. More selling.", "少看字，多成交。")}</p>
                </div>
                <Button onClick={onNew} data-testid="sb-new-btn" className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-10 px-5">
                    <Plus className="mr-2 h-4 w-4" strokeWidth={1.75} /> {tt(lang, "New client", "新客户")}
                </Button>
            </div>
            {rows === null ? <div className="py-16 text-center text-cafe-mocha text-sm">…</div>
                : rows.length === 0 ? (
                    <div className="py-16 text-center border border-dashed border-cafe-espresso/20 rounded-2xl mt-6">
                        <Brain className="h-8 w-8 mx-auto text-cafe-olive/50" strokeWidth={1.5} />
                        <p className="mt-3 text-cafe-mocha text-sm">{tt(lang, "No clients yet — fill the input form to start", "还没有客户 — 填写输入表单开始分析")}</p>
                    </div>
                ) : (
                    <div className="grid gap-3 mt-6">
                        {rows.map((r) => (
                            <div key={r.id} onClick={() => onOpen(r.id)} role="button" tabIndex={0}
                                onKeyDown={(e) => { if (e.key === "Enter") onOpen(r.id); }}
                                data-testid="sb-lead-card"
                                className="cursor-pointer text-left rounded-2xl border border-cafe-espresso/12 bg-white/60 hover:bg-white transition-colors p-5">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                    <span className="font-serif text-lg text-cafe-espresso">{r.business_name}</span>
                                    {r.source === "website_inquiry" && (
                                        <span className="ml-2 align-middle inline-block text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full bg-cafe-olive/12 text-cafe-olive border border-cafe-olive/30" data-testid={`lead-website-badge-${r.id}`}>Website</span>
                                    )}
                                    {r.form?.industry && <span className="text-[11px] text-cafe-mocha">{r.form.industry}</span>}
                                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-cafe-latte text-cafe-mocha">
                                        {(() => { const s = STAGES.find((x) => x[0] === r.pipeline_stage); return s ? tt(lang, s[1], s[2]) : r.pipeline_stage; })()}
                                    </span>
                                    {r.status === "generating" && (
                                        <span className="text-[11px] text-cafe-olive flex items-center gap-1">
                                            <Loader2 className="h-3 w-3 animate-spin" /> {tt(lang, ...(STEP_LABELS[r.current_step] || [r.current_step, r.current_step]))} · {r.progress}%
                                        </span>)}
                                    {r.status === "error" && <span className="text-[11px] text-red-700">{tt(lang, "Failed — open to retry", "生成失败 — 点开重试")}</span>}
                                    <span className="ml-auto flex items-center gap-3 text-[12px]">
                                        {r.close_plan?.close_rate != null && (
                                            <span className="font-semibold" style={{ color: r.close_plan.close_rate >= 70 ? "#5B7A3D" : r.close_plan.close_rate >= 45 ? "#B8863B" : "#B0503C" }}>
                                                {tt(lang, "Close", "成交")} {r.close_plan.close_rate}%</span>)}
                                        <MoreActions
                                            testid={`sb-more-${r.id}`}
                                            subject={`${tt(lang, "Lead", "客户")} "${r.business_name}"`}
                                            items={[{
                                                label: tt(lang, "Delete lead", "删除客户"), danger: true, testid: "sb-delete-btn",
                                                confirm: tt(lang, "Permanently deletes this lead, its analysis and proposal. This cannot be undone.", "永久删除该客户及其分析和提案，无法恢复。"),
                                                confirmLabel: tt(lang, "Delete", "删除"),
                                                onClick: () => del(r.id),
                                            }]}
                                        />
                                    </span>
                                </div>
                                {r.close_plan?.today_action_cn && (
                                    <p className="mt-2 text-[13px] text-cafe-espresso/80 flex items-start gap-1.5">
                                        <Zap className="h-3.5 w-3.5 mt-0.5 text-cafe-olive shrink-0" />
                                        <span><b>{tt(lang, "Today:", "今日：")}</b> {lang === "en" ? r.close_plan.today_action_en : r.close_plan.today_action_cn}</span>
                                    </p>)}
                            </div>
                        ))}
                    </div>
                )}
        </div>
    );
}

// ---------- V9 photographer input form ----------
const GOALS = [["walkins", "More walk-in customers", "更多到店客人"], ["awareness", "Brand awareness", "打响品牌知名度"],
["newproduct", "Promote new product", "推广新品"], ["followers", "Grow followers", "涨粉"], ["launch", "Opening launch", "开业宣传"]];
const SHOOT_TYPES = [["reels", "Reels (video)", "短视频 Reels"], ["photos", "Photos", "照片"],
["both", "Reels + Photos", "短视频 + 照片"], ["menu", "Menu shoot", "菜单拍摄"], ["brand", "Brand story", "品牌形象"]];
const BUDGETS = ["< RM500", "RM500 – RM1,000", "RM1,000 – RM3,000", "RM3,000+", tt("cn", "Unknown", "未知")];

function Field({ label, required, children }) {
    return (
        <div>
            <p className="text-[11.5px] font-medium text-cafe-espresso mb-1.5">{label}{required && <span className="text-red-600"> *</span>}</p>
            {children}
        </div>
    );
}

function NewLeadForm({ onDone }) {
    const lang = useLang();
    const [files, setFiles] = useState([]);
    const [form, setForm] = useState({ brand_name: "", industry: "", goal: "", shoot_type: "", hours: "", budget: "", notes: "", instagram_url: "", whatsapp_text: "" });
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    const inputCls = "w-full rounded-xl border border-cafe-espresso/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-cafe-olive";
    const submit = async () => {
        if (!form.brand_name.trim()) { toast.error(tt(lang, "Brand name is required", "品牌名必填")); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            const { data } = await axios.post(`${API}/sales-brain/leads`, fd, { headers: studioHeaders() });
            onDone(data.id);
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setBusy(false); }
    };
    return (
        <div className="max-w-2xl" data-testid="sb-new-form">
            <button onClick={() => onDone(null)} className="flex items-center gap-1.5 text-sm text-cafe-mocha hover:text-cafe-espresso mb-6" data-testid="sb-form-back">
                <ArrowLeft className="h-4 w-4" /> {tt(lang, "Back", "返回")}
            </button>
            <h2 className="font-serif text-3xl text-cafe-espresso">{tt(lang, "New client — input form", "新客户 — 输入表单")}</h2>
            <p className="text-sm text-cafe-mocha mt-2 mb-6">{tt(lang, "Fill this first — AI analyzes with YOUR context, not guesses.", "先填这个表 — AI 用你的真实情报分析，不瞎猜。")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={tt(lang, "Brand name", "品牌名")} required>
                    <input value={form.brand_name} onChange={set("brand_name")} data-testid="sb-form-brand" placeholder={tt(lang, "e.g. Mokky's Pizza", "例：Mokky's Pizza")} className={inputCls} />
                </Field>
                <Field label={tt(lang, "Industry", "行业")}>
                    <input value={form.industry} onChange={set("industry")} data-testid="sb-form-industry" placeholder={tt(lang, "e.g. Specialty cafe / Hotpot", "例：咖啡店 / 火锅店 / 烘焙")} className={inputCls} />
                </Field>
                <Field label={tt(lang, "Client's goal", "客户目标")}>
                    <select value={form.goal} onChange={set("goal")} data-testid="sb-form-goal" className={inputCls}>
                        <option value="">{tt(lang, "Select…", "选择…")}</option>
                        {GOALS.map(([v, en, zh]) => <option key={v} value={tt(lang, en, zh)}>{tt(lang, en, zh)}</option>)}
                    </select>
                </Field>
                <Field label={tt(lang, "Shoot type", "拍摄类型")}>
                    <select value={form.shoot_type} onChange={set("shoot_type")} data-testid="sb-form-shoot-type" className={inputCls}>
                        <option value="">{tt(lang, "Select…", "选择…")}</option>
                        {SHOOT_TYPES.map(([v, en, zh]) => <option key={v} value={tt(lang, en, zh)}>{tt(lang, en, zh)}</option>)}
                    </select>
                </Field>
                <Field label={tt(lang, "Hours available", "可拍时数")}>
                    <select value={form.hours} onChange={set("hours")} data-testid="sb-form-hours" className={inputCls}>
                        <option value="">{tt(lang, "Select…", "选择…")}</option>
                        {["2", "4", "6", "8"].map((h) => <option key={h} value={`${h}h`}>{h} {tt(lang, "hours", "小时")}</option>)}
                    </select>
                </Field>
                <Field label={tt(lang, "Client budget", "客户预算")}>
                    <select value={form.budget} onChange={set("budget")} data-testid="sb-form-budget" className={inputCls}>
                        <option value="">{tt(lang, "Select…", "选择…")}</option>
                        {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                </Field>
            </div>
            <div className="mt-4">
                <Field label={tt(lang, "Notes", "备注")}>
                    <textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="sb-form-notes"
                        placeholder={tt(lang, "Anything else: urgency, vibe, constraints…", "其他情报：紧急程度、调性、限制条件…")} className={inputCls} />
                </Field>
            </div>
            <div className="mt-6 rounded-2xl border border-cafe-espresso/12 bg-cafe-latte/25 p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-mocha mb-3">{tt(lang, "Optional — more fuel for the AI", "选填 — 给 AI 更多弹药")}</p>
                <div className="grid gap-3">
                    <input value={form.instagram_url} onChange={set("instagram_url")} data-testid="sb-form-ig"
                        placeholder={tt(lang, "Instagram profile link (AI auto-fetches)", "Instagram 主页链接（AI 自动抓取粉丝/帖子）")} className={inputCls} />
                    <textarea value={form.whatsapp_text} onChange={set("whatsapp_text")} rows={2} data-testid="sb-form-whatsapp"
                        placeholder={tt(lang, "Paste WhatsApp conversation", "粘贴 WhatsApp 对话")} className={inputCls} />
                    <div onClick={() => fileRef.current?.click()}
                        className="rounded-xl border-2 border-dashed border-cafe-olive/40 bg-white/50 hover:bg-white transition-colors cursor-pointer p-4 text-center">
                        <Upload className="h-5 w-5 mx-auto text-cafe-olive" strokeWidth={1.5} />
                        <p className="mt-1.5 text-[12.5px] text-cafe-espresso">{tt(lang, "Screenshots / menu PDF (max 8)", "截图 / 菜单 PDF（最多 8 个）")}</p>
                        <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="sb-upload-input"
                            onChange={(e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 8))} />
                    </div>
                    {files.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {files.map((f, i) => (
                                <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-white text-cafe-espresso flex items-center gap-1.5">
                                    {f.name.slice(0, 28)}<button onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-cafe-mocha hover:text-red-700">×</button>
                                </span>))}
                        </div>)}
                </div>
            </div>
            <Button onClick={submit} disabled={busy || !form.brand_name.trim()} data-testid="sb-submit" className="mt-6 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-11 px-8">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> …</> : <><Brain className="mr-2 h-4 w-4" /> {tt(lang, "Start 5-step analysis", "启动 5 步分析")}</>}
            </Button>
        </div>
    );
}

// ---------- detail ----------
function LeadDetail({ id, onBack }) {
    const lang = useLang();
    const [doc, setDoc] = useState(null);
    const timer = useRef(null);
    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/sales-brain/leads/${id}`, { headers: studioHeaders() });
            setDoc(data);
            if (data.status === "generating") timer.current = setTimeout(load, 3000);
        } catch { toast.error("Load failed"); }
    };
    useEffect(() => { load(); return () => clearTimeout(timer.current); }, [id]); // eslint-disable-line

    const retry = async () => {
        await axios.post(`${API}/sales-brain/leads/${id}/retry`, null, { headers: studioHeaders() });
        setDoc((d) => ({ ...d, status: "generating", error: null }));
        clearTimeout(timer.current); timer.current = setTimeout(load, 2500);
    };
    const setStage = async (stage) => {
        await axios.put(`${API}/sales-brain/leads/${id}/stage`, { stage }, { headers: studioHeaders() });
        load();
    };
    const share = async () => {
        const { data } = await axios.post(`${API}/sales-brain/leads/${id}/share`, null, { headers: studioHeaders() });
        copyText(`${window.location.origin}${data.path}`, tt(lang, "Proposal link copied", "提案链接已复制 — 可直接发给客户"));
    };

    if (!doc) return <div className="py-16 text-center text-cafe-mocha text-sm">…</div>;
    const ready = doc.status === "ready";

    return (
        <div data-testid="sb-detail" className="pb-10">
            <div className="flex flex-wrap items-center gap-4 mb-6">
                <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-cafe-mocha hover:text-cafe-espresso" data-testid="sb-back-btn">
                    <ArrowLeft className="h-4 w-4" /> {tt(lang, "Back", "返回")}
                </button>
                <h2 className="font-serif text-3xl text-cafe-espresso">{doc.business_name}</h2>
                {doc.snapshot && <span className="text-[12px] text-cafe-mocha">{cx(lang, doc.snapshot, "industry")} · {doc.snapshot.location}</span>}
                <select value={doc.pipeline_stage || "new"} onChange={(e) => setStage(e.target.value)} data-testid="sb-stage-select"
                    className="ml-auto rounded-full border border-cafe-espresso/15 bg-white px-3 py-1.5 text-[12px] outline-none">
                    {STAGES.map(([v, en, zh]) => <option key={v} value={v}>{tt(lang, en, zh)}</option>)}
                </select>
            </div>

            {doc.status === "generating" && <JobProgress doc={doc} />}
            {doc.status === "error" && (
                <div className="rounded-2xl border border-red-300 bg-red-50 p-5 mb-6" data-testid="sb-error">
                    <p className="text-sm text-red-800">{doc.error}</p>
                    <Button onClick={retry} data-testid="sb-retry-btn" className="mt-3 rounded-full bg-cafe-olive text-cafe-cream h-9 px-5 text-[13px]">
                        <RefreshCw className="mr-2 h-3.5 w-3.5" /> {tt(lang, "Retry (resumes from last step)", "重新生成（从上次步骤继续）")}
                    </Button>
                </div>)}

            {ready && <ActionDashboard doc={doc} onShare={share} />}
            {ready && <DocSettings doc={doc} onUpdate={setDoc} />}
            {ready && doc.audit && <AuditCard au={doc.audit} pain={doc.pain} />}
            {ready && doc.creative && <Storyboard doc={doc} onUpdate={setDoc} />}
            {ready && doc.proposal && <ProposalPreview p={doc.proposal} onShare={share} />}
            {ready && <QuoteEditor leadId={doc.id} />}
            {ready && doc.close_plan?.objections?.length > 0 && <Objections obs={doc.close_plan.objections} />}

            {ready && (
                <Collapse icon={MessageCircle} title={tt(lang, "AI Sales Advisor — chat", "AI 销售顾问 — 随时提问")} testid="sb-lvl-chat"
                    sub={tt(lang, '"How do I close?" / "Client says expensive"', "「怎么成交？」「客户嫌贵怎么回？」")}>
                    <SalesChatPanel leadId={doc.id} />
                </Collapse>)}
            {ready && (
                <Collapse icon={Phone} title={tt(lang, "Client Portal access", "客户门户 — 让客户自己看报告")} testid="sb-lvl-portal"
                    sub={doc.portal?.enabled ? tt(lang, `Active · ${doc.portal.phone}`, `已开通 · ${doc.portal.phone}`) : tt(lang, "Not set up yet", "还未开通")}>
                    <PortalAccess lead={doc} onUpdate={setDoc} />
                </Collapse>)}
        </div>
    );
}

function JobProgress({ doc }) {
    const lang = useLang();
    const idx = STEP_ORDER.indexOf(doc.current_step);
    return (
        <div className="rounded-2xl border border-cafe-olive/25 bg-cafe-latte/40 p-6 mb-6" data-testid="sb-progress">
            <div className="flex items-center gap-3 mb-4">
                <Loader2 className="h-5 w-5 animate-spin text-cafe-olive" />
                <p className="text-sm font-medium text-cafe-espresso">{tt(lang, ...(STEP_LABELS[doc.current_step] || [doc.current_step, doc.current_step]))}</p>
                <span className="ml-auto font-serif text-2xl text-cafe-olive">{doc.progress}%</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
                {STEP_ORDER.map((s, i) => (
                    <div key={s} className="text-center">
                        <div className={`h-1.5 rounded-full ${i < idx ? "bg-cafe-olive" : i === idx ? "bg-cafe-olive/50 animate-pulse" : "bg-white"}`} />
                        <p className="text-[10px] text-cafe-mocha mt-1.5">{tt(lang, ["Input", "Analyze", "Proposal", "Price", "Close"][i], ["输入", "分析", "提案", "报价", "成交"][i])}</p>
                    </div>))}
            </div>
        </div>
    );
}

// ---------- V9 single-screen action dashboard ----------
function ActionDashboard({ doc, onShare }) {
    const lang = useLang();
    const cp = doc.close_plan || {};
    const best = doc.price?.best_package || {};
    const qeTotal = doc.quote_editable?.total;
    const price = qeTotal != null && qeTotal > 0 ? `RM ${Number(qeTotal).toLocaleString()}` : (best.price || "—");
    const wa = lang === "en" ? cp.whatsapp_en : cp.whatsapp_cn || cp.whatsapp_en;
    const rate = cp.close_rate;
    const openDoc = (t) => window.open(`/studio/docs/${doc.id}/${t}?lang=${lang}`, "_blank");
    const DOCS = [
        ["audit", "📊", tt(lang, "Audit", "审计"), !!doc.audit],
        ["creative", "🎬", tt(lang, "Storyboard", "分镜"), !!doc.creative],
        ["proposal", "📄", tt(lang, "Proposal", "提案"), !!doc.proposal],
        ["quotation", "💰", tt(lang, "Quotation", "报价"), !!doc.price],
    ];
    return (
        <div className="rounded-2xl bg-cafe-espresso text-cafe-cream p-6 mb-5" data-testid="sb-action-dashboard">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="rounded-xl bg-white/8 p-4" data-testid="dash-pain">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#E08A6D] font-bold flex items-center gap-1.5 mb-2">
                        <Flame className="h-3.5 w-3.5" /> {tt(lang, "Biggest pain", "最大痛点")}</p>
                    <p className="text-[13.5px] leading-relaxed">{li(lang, doc.pain)}</p>
                </div>
                <div className="rounded-xl bg-white/8 p-4" data-testid="dash-package">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#A8C686] font-bold flex items-center gap-1.5 mb-2">
                        <Star className="h-3.5 w-3.5" /> {tt(lang, "Best package", "最佳配套")}</p>
                    <p className="text-[14px] font-semibold">{best.name || "—"}</p>
                    <p className="font-serif text-lg text-[#E0B84C]">{price}</p>
                    <p className="text-[11.5px] text-cafe-cream/70 mt-1">{cx(lang, best, "reason")}</p>
                </div>
                <div className="rounded-xl bg-white/8 p-4" data-testid="dash-close-rate">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-cream/60 font-bold mb-2">{tt(lang, "Est. close rate", "预估成交率")}</p>
                    <p className="font-serif text-4xl leading-none" style={{ color: rate >= 70 ? "#A8C686" : rate >= 45 ? "#E0B84C" : "#E08A6D" }}>{rate != null ? `${rate}%` : "—"}</p>
                    <p className="text-[11.5px] text-cafe-cream/70 mt-1.5">{cx(lang, cp, "close_rate_reason")}</p>
                </div>
            </div>
            <div className="rounded-xl border border-[#E0B84C]/40 bg-[#E0B84C]/10 p-4 mb-4" data-testid="dash-today-action">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E0B84C] font-bold flex items-center gap-1.5 mb-2">
                    <Zap className="h-3.5 w-3.5" /> {tt(lang, "Today's action", "今日行动")}</p>
                <p className="text-[14.5px] font-medium">{cx(lang, cp, "today_action")}</p>
                {wa && <p className="mt-2.5 text-[13px] text-cafe-cream/85 whitespace-pre-wrap rounded-lg bg-black/20 p-3">{wa}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                    {wa && <button onClick={() => copyText(wa)} data-testid="dash-copy-whatsapp"
                        className="rounded-full bg-[#E0B84C] text-cafe-espresso px-4 py-1.5 text-[12px] font-semibold flex items-center gap-1.5">
                        <Copy className="h-3 w-3" /> {tt(lang, "Copy WhatsApp", "复制 WhatsApp 话术")}</button>}
                    <button onClick={onShare} className="rounded-full border border-cafe-cream/30 px-4 py-1.5 text-[12px] flex items-center gap-1.5" data-testid="dash-share">
                        <Share2 className="h-3 w-3" /> {tt(lang, "Proposal link", "提案链接")}</button>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-cream/60 font-bold" data-testid="dash-ready-docs">{tt(lang, "Ready docs", "文件就绪")}</p>
                {DOCS.map(([t, ic, label, ok]) => (
                    <button key={t} onClick={() => ok && openDoc(t)} disabled={!ok} data-testid={`dash-doc-${t}`}
                        className={`flex items-center gap-1.5 text-[12px] rounded-full px-3 py-1.5 border transition-colors ${ok ? "border-cafe-cream/25 hover:bg-white/10" : "border-cafe-cream/10 text-cafe-cream/40"}`}>
                        {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#A8C686]" /> : <Circle className="h-3.5 w-3.5" />} {ic} {label}
                    </button>))}
                <p className="ml-auto text-[12.5px] text-cafe-cream/80 flex items-center gap-1.5" data-testid="dash-next-step">
                    <span className="text-[#E0B84C]">→</span> <b>{tt(lang, "Next:", "下一步：")}</b> {cx(lang, cp, "next_step")}</p>
            </div>
        </div>
    );
}

// ---------- lean audit: 5 stars + top 5 problems ----------
function AuditCard({ au, pain }) {
    const lang = useLang();
    const r = au.ratings || {};
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 p-5 mb-4" data-testid="sb-audit-card">
            <p className="text-[12px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-4">📊 {tt(lang, "Lean audit", "极简审计")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="grid gap-2.5">
                    {RATING_LABELS.map(([k, en, zh]) => (
                        <div key={k} className="flex items-center gap-3 text-[13px]" data-testid={`audit-rating-${k}`}>
                            <span className="w-24 shrink-0 text-cafe-mocha">{tt(lang, en, zh)}</span>
                            <Stars v={r[k]} />
                            <span className="text-[11px] text-cafe-mocha/70">{r[k]}/5</span>
                        </div>))}
                </div>
                <div>
                    <p className="text-[11px] uppercase tracking-wider text-red-800 mb-2">{tt(lang, "Top 5 problems", "五大问题")}</p>
                    <ol className="space-y-1.5 text-[13px]">
                        {(au.problems || []).map((p, i) => (
                            <li key={i} className="flex gap-2" data-testid={`audit-problem-${i}`}>
                                <b className="text-red-700 shrink-0">{i + 1}.</b><span>{li(lang, p)}</span></li>))}
                    </ol>
                </div>
            </div>
        </section>
    );
}

// ---------- creative direction: free teaser + 6 videos + "My Idea" merge ----------
function Storyboard({ doc, onUpdate }) {
    const lang = useLang();
    const cr = doc.creative || {};
    const ds = doc.doc_settings || {};
    const included = ds.videos_included;
    const isIncluded = (no) => !included || included.includes(no);
    const [idea, setIdea] = useState(cr.my_idea || "");
    const [busy, setBusy] = useState(!!doc.creative_merging);
    const pollMerge = async () => {
        for (let i = 0; i < 75; i++) {
            await new Promise((r) => setTimeout(r, 4000));
            try {
                const { data } = await axios.get(`${API}/sales-brain/leads/${doc.id}`, { headers: studioHeaders() });
                if (!data.creative_merging) {
                    if (data.creative_merge_error) toast.error(data.creative_merge_error);
                    else {
                        onUpdate(data);
                        toast.success(tt(lang, "Storyboard merged with your idea", "企划已按你的想法合并重生成"));
                    }
                    setBusy(false);
                    return;
                }
            } catch { /* keep polling */ }
        }
        toast.error(tt(lang, "Still generating — refresh in a minute", "还在生成中 — 稍后刷新页面查看"));
        setBusy(false);
    };
    useEffect(() => { if (doc.creative_merging) pollMerge(); }, []); // eslint-disable-line
    const [expanded, setExpanded] = useState({});
    const [editingNo, setEditingNo] = useState(null);
    const saveVideo = async (video) => {
        const videos = (cr.videos || []).map((v) => (v.no === video.no ? video : v));
        try {
            const { data } = await axios.put(`${API}/sales-brain/leads/${doc.id}/creative`,
                { ...cr, videos }, { headers: studioHeaders() });
            onUpdate({ ...doc, creative: data.creative });
            setEditingNo(null);
            toast.success(tt(lang, "Script saved — PDF will match exactly", "脚本已保存 — PDF 与你修改的一致"));
        } catch { toast.error(tt(lang, "Save failed", "保存失败")); }
    };
    const saveIncludes = async (next) => {
        const { data } = await axios.put(`${API}/sales-brain/leads/${doc.id}/doc-settings`,
            { show_prices: ds.show_prices !== false, show_partner_logo: !!ds.show_partner_logo, videos_included: next },
            { headers: studioHeaders() });
        onUpdate({ ...doc, doc_settings: data.doc_settings });
    };
    const toggleVideo = (no) => {
        const all = (cr.videos || []).map((v) => v.no);
        const cur = included ? [...included] : all;
        saveIncludes(cur.includes(no) ? cur.filter((x) => x !== no) : [...cur, no].sort((a, b) => a - b));
    };
    const merge = async () => {
        if (!idea.trim()) { toast.error(tt(lang, "Write your idea first", "先写下你的想法")); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/sales-brain/leads/${doc.id}/creative/merge`, { my_idea: idea }, { headers: studioHeaders() });
            await pollMerge();
        } catch (e) {
            toast.error(e?.response?.data?.detail || tt(lang, "Merge failed", "合并失败"));
            setBusy(false);
        }
    };
    const ft = cr.free_teaser;
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 p-4 sm:p-5 mb-4" data-testid="sb-storyboard">
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <p className="text-[12px] uppercase tracking-[0.2em] text-cafe-olive font-semibold flex items-center gap-1.5">
                    <Clapperboard className="h-4 w-4" /> {tt(lang, `Creative Direction — ${(cr.videos || []).length || ""} videos`, `创意方向 — ${(cr.videos || []).length || ""} 个视频企划`)}</p>
                <span className="font-serif text-[14px] text-cafe-espresso ml-1">{cx(lang, cr, "title")}</span>
            </div>
            <div className="rounded-xl bg-cafe-latte/40 p-3.5 mb-3 text-[13px]" data-testid="sb-ai-suggestion">
                <b className="text-cafe-olive">🤖 {tt(lang, "AI suggestion:", "AI 建议：")}</b> {cx(lang, cr, "ai_suggestion")}
            </div>
            {ft && (
                <div className="rounded-xl border-2 border-[#B8863B]/50 bg-[#B8863B]/8 p-4 mb-4" data-testid="sb-free-teaser">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#B8863B] font-bold mb-2">
                        🎁 {tt(lang, "Free 15s teaser — shoot on the spot, prove your value", "免费 15 秒试拍 — 到店当场拍，先证明价值")}</p>
                    <p className="text-[13.5px] font-medium text-cafe-espresso mb-2">{cx(lang, ft, "concept")}</p>
                    <ul className="space-y-1 text-[12.5px] text-cafe-espresso/85 mb-2">
                        {(ft.shots || []).map((s, i) => <li key={i}>🎬 {li(lang, s)}</li>)}
                    </ul>
                    <p className="text-[12px] text-cafe-mocha italic">💬 {cx(lang, ft, "pitch")}</p>
                    <button onClick={() => copyText(lang === "en" ? ft.pitch_en : ft.pitch_cn || ft.pitch_en)}
                        className="mt-2 text-[11px] text-cafe-olive flex items-center gap-1 hover:underline" data-testid="sb-teaser-copy">
                        <Copy className="h-3 w-3" /> {tt(lang, "Copy pitch line", "复制开场话术")}</button>
                </div>)}
            {!cr.videos && (
                <p className="text-[12.5px] text-cafe-mocha rounded-lg bg-cafe-latte/40 p-3 mb-3">
                    {tt(lang, "This lead uses the old storyboard format — type an idea below and hit Merge to upgrade it to the 10-video plan.",
                        "这个客户还是旧版分镜 — 在下方写一句想法后点「合并重生成」即可升级为 10 视频企划。")}</p>)}
            <div className="grid gap-3 mb-4">
                {(cr.videos || []).map((v) => (
                    <div key={v.no} className={`rounded-xl border p-3.5 transition-opacity ${isIncluded(v.no) ? "border-cafe-espresso/12 bg-white" : "border-dashed border-cafe-espresso/15 bg-white/40 opacity-55"}`}
                        data-testid={`sb-video-${v.no}`}>
                        <div className="flex flex-wrap items-start gap-2.5">
                            <label className="flex items-center gap-1.5 cursor-pointer shrink-0 mt-0.5" title={tt(lang, "Show in PDF & proposal", "呈现在 PDF 和提案里")}>
                                <input type="checkbox" checked={isIncluded(v.no)} onChange={() => toggleVideo(v.no)}
                                    className="h-4 w-4 accent-[#5B6B3C]" data-testid={`sb-video-check-${v.no}`} />
                                <span className="text-[10px] uppercase tracking-wider text-cafe-mocha">PDF</span>
                            </label>
                            <div className="min-w-0 flex-1">
                                <p className="font-serif text-[15px] text-cafe-espresso">
                                    <span className="text-cafe-olive">V{v.no}</span> · {li(lang, { en: v.title_en, cn: v.title_cn })}</p>
                                <p className="text-[12.5px] text-cafe-mocha mt-0.5"><b>{tt(lang, "Purpose:", "目的：")}</b> {li(lang, { en: v.purpose_en, cn: v.purpose_cn })}</p>
                                {(v.hook_en || v.hook_cn) && <p className="text-[12px] text-cafe-espresso/70 mt-0.5">🪝 {li(lang, { en: v.hook_en, cn: v.hook_cn })}</p>}
                            </div>
                            <button onClick={() => setEditingNo(editingNo === v.no ? null : v.no)} data-testid={`sb-video-edit-${v.no}`}
                                className="text-[11.5px] text-cafe-mocha flex items-center gap-1 shrink-0 hover:text-cafe-espresso hover:underline">
                                ✏️ {tt(lang, "Edit", "编辑")}
                            </button>
                            <button onClick={() => setExpanded((p) => ({ ...p, [v.no]: !p[v.no] }))} data-testid={`sb-video-expand-${v.no}`}
                                className="text-[11.5px] text-cafe-olive flex items-center gap-1 shrink-0 hover:underline">
                                {tt(lang, "Script", "拍摄脚本")} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded[v.no] ? "rotate-180" : ""}`} />
                            </button>
                        </div>
                        {editingNo === v.no && <VideoEditor video={v} onSave={saveVideo} onCancel={() => setEditingNo(null)} />}
                        {expanded[v.no] && editingNo !== v.no && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {(v.scenes || []).map((s) => (
                                    <div key={s.no} className="rounded-lg bg-cafe-latte/30 p-3 text-[12px]">
                                        <p className="font-medium text-cafe-olive mb-1">Scene {s.no} {s.duration && <span className="text-[10.5px] text-cafe-mocha font-normal">· ⏱ {s.duration}</span>}</p>
                                        <p className="mb-0.5">{li(lang, { en: s.shot_en, cn: s.shot_cn })}</p>
                                        {(s.lens || s.movement) && <p className="text-cafe-mocha">🔭 {s.lens} · 🎥 {s.movement}</p>}
                                        {(s.voiceover_en || s.voiceover_cn) && <p className="text-cafe-espresso/75 mt-0.5">🎙 {li(lang, { en: s.voiceover_en, cn: s.voiceover_cn })}</p>}
                                    </div>))}
                            </div>)}
                    </div>))}
            </div>
            <div className="rounded-xl border border-cafe-olive/30 bg-cafe-olive/5 p-4" data-testid="sb-my-idea-box">
                <p className="text-[11.5px] font-semibold text-cafe-espresso mb-2">✍️ {tt(lang, "My idea (location reality, props, constraints) — AI merges it in", "我的想法（现场实况、道具、限制）— AI 合并重生成")}</p>
                <textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={2} data-testid="sb-my-idea-input"
                    placeholder={tt(lang, 'e.g. "no drone, shoot inside only, owner will appear"', '例："不能航拍、只能店内拍、老板愿意出镜"')}
                    className="w-full rounded-lg border border-cafe-espresso/12 bg-white px-3 py-2.5 text-[12.5px] outline-none focus:border-cafe-olive" />
                <Button onClick={merge} disabled={busy} data-testid="sb-merge-btn" className="mt-2.5 rounded-full bg-cafe-olive text-cafe-cream h-8 px-4 text-[12px]">
                    {busy ? <><Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> {tt(lang, "Merging…", "合并中…")}</> : <><Sparkles className="mr-1.5 h-3 w-3" /> {tt(lang, "Merge & regenerate", "合并重生成企划")}</>}
                </Button>
            </div>
        </section>
    );
}

// ---------- per-video script editor (edits render 1:1 in PDFs) ----------
function VideoEditor({ video, onSave, onCancel }) {
    const lang = useLang();
    const [v, setV] = useState(() => JSON.parse(JSON.stringify(video)));
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
    const setScene = (i, k) => (e) => setV((p) => ({ ...p, scenes: p.scenes.map((s, j) => (j === i ? { ...s, [k]: e.target.value } : s)) }));
    const inp = "w-full rounded-md border border-cafe-espresso/12 bg-white px-2 py-1.5 text-[12px] outline-none focus:border-cafe-olive";
    const Lbl = ({ children }) => <p className="text-[10px] uppercase tracking-wider text-cafe-mocha mb-1">{children}</p>;
    const Duo = ({ label, keyBase, rows }) => (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><Lbl>{label} EN</Lbl>{rows
                ? <textarea rows={rows} value={v[`${keyBase}_en`] || ""} onChange={set(`${keyBase}_en`)} className={inp} />
                : <input value={v[`${keyBase}_en`] || ""} onChange={set(`${keyBase}_en`)} className={inp} />}</div>
            <div><Lbl>{label} 中文</Lbl>{rows
                ? <textarea rows={rows} value={v[`${keyBase}_cn`] || ""} onChange={set(`${keyBase}_cn`)} className={inp} />
                : <input value={v[`${keyBase}_cn`] || ""} onChange={set(`${keyBase}_cn`)} className={inp} />}</div>
        </div>
    );
    const save = async () => { setBusy(true); await onSave(v); setBusy(false); };
    return (
        <div className="mt-3 rounded-xl border border-cafe-olive/30 bg-cafe-olive/5 p-4 grid gap-3" data-testid={`sb-video-editor-${video.no}`}>
            <Duo label={tt(lang, "Title", "视频名")} keyBase="title" />
            <Duo label={tt(lang, "Purpose", "目的")} keyBase="purpose" />
            <Duo label={tt(lang, "Hook", "开场 Hook")} keyBase="hook" />
            {(v.scenes || []).map((s, i) => (
                <div key={i} className="rounded-lg bg-white border border-cafe-espresso/10 p-3 grid gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-[12px] font-semibold text-cafe-olive">Scene {s.no}</p>
                        <div className="flex items-center gap-1.5 text-[11px] text-cafe-mocha">🔭<input value={s.lens || ""} onChange={setScene(i, "lens")} placeholder="lens" className={`${inp} w-24`} /></div>
                        <div className="flex items-center gap-1.5 text-[11px] text-cafe-mocha">🎥<input value={s.movement || ""} onChange={setScene(i, "movement")} placeholder={tt(lang, "movement", "运镜")} className={`${inp} w-32`} /></div>
                        <div className="flex items-center gap-1.5 text-[11px] text-cafe-mocha">⏱<input value={s.duration || ""} onChange={setScene(i, "duration")} placeholder="3s" className={`${inp} w-16`} /></div>
                        <button onClick={() => setV((p) => ({ ...p, scenes: p.scenes.filter((_, j) => j !== i) }))}
                            className="ml-auto text-cafe-mocha/50 hover:text-red-700" title={tt(lang, "Delete scene", "删除此场")} data-testid={`sb-scene-del-${video.no}-${i}`}>
                            <Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div><Lbl>{tt(lang, "Shot", "镜头画面")} EN</Lbl><textarea rows={2} value={s.shot_en || ""} onChange={setScene(i, "shot_en")} className={inp} /></div>
                        <div><Lbl>{tt(lang, "Shot", "镜头画面")} 中文</Lbl><textarea rows={2} value={s.shot_cn || ""} onChange={setScene(i, "shot_cn")} className={inp} /></div>
                        <div><Lbl>{tt(lang, "Voiceover", "旁白")} EN</Lbl><input value={s.voiceover_en || ""} onChange={setScene(i, "voiceover_en")} className={inp} /></div>
                        <div><Lbl>{tt(lang, "Voiceover", "旁白")} 中文</Lbl><input value={s.voiceover_cn || ""} onChange={setScene(i, "voiceover_cn")} className={inp} /></div>
                    </div>
                </div>))}
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => setV((p) => ({ ...p, scenes: [...(p.scenes || []), { no: (p.scenes?.length || 0) + 1, shot_en: "", shot_cn: "", lens: "", movement: "", voiceover_en: "", voiceover_cn: "", duration: "" }] }))}
                    className="text-[12px] text-cafe-olive flex items-center gap-1 hover:underline" data-testid={`sb-scene-add-${video.no}`}>
                    <Plus className="h-3.5 w-3.5" /> {tt(lang, "Add scene", "加一场")}</button>
                <div className="ml-auto flex gap-2">
                    <Button onClick={onCancel} variant="outline" className="rounded-full border-cafe-espresso/20 h-8 px-4 text-[12px]" data-testid={`sb-video-cancel-${video.no}`}>
                        {tt(lang, "Cancel", "取消")}</Button>
                    <Button onClick={save} disabled={busy} className="rounded-full bg-cafe-olive text-cafe-cream h-8 px-5 text-[12px]" data-testid={`sb-video-save-${video.no}`}>
                        {busy ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Save className="mr-1.5 h-3 w-3" />} {tt(lang, "Save script", "保存脚本")}</Button>
                </div>
            </div>
        </div>
    );
}

// ---------- proposal settings: prices on/off · partner logo on/off ----------
function DocSettings({ doc, onUpdate }) {
    const lang = useLang();
    const ds = doc.doc_settings || {};
    const [partner, setPartner] = useState(null);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => {
        axios.get(`${API}/sales-brain/partner-logo`, { headers: studioHeaders() }).then((r) => setPartner(r.data)).catch(() => {});
    }, []);
    const save = async (patch) => {
        setBusy(true);
        try {
            const { data } = await axios.put(`${API}/sales-brain/leads/${doc.id}/doc-settings`,
                {
                    show_prices: ds.show_prices !== false, show_partner_logo: !!ds.show_partner_logo,
                    show_ai_system: ds.show_ai_system !== false, include_menu_shoot: !!ds.include_menu_shoot,
                    videos_included: ds.videos_included ?? null, ...patch,
                },
                { headers: studioHeaders(), timeout: 120000 });
            onUpdate({
                ...doc, doc_settings: data.doc_settings,
                ...(data.proposal_alt ? { proposal_alt: data.proposal_alt } : {}),
                ...(data.menu_shoot_reco ? { menu_shoot_reco: data.menu_shoot_reco } : {}),
            });
        } catch { toast.error(tt(lang, "Save failed", "保存失败")); }
        setBusy(false);
    };
    const uploadLogo = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await axios.post(`${API}/sales-brain/partner-logo`, fd, { headers: studioHeaders() });
            setPartner((p) => ({ ...(p || {}), logo_url: data.logo_url }));
            if (ds.show_partner_logo) onUpdate({ ...doc, doc_settings: { ...ds, partner_logo_url: data.logo_url } });
            toast.success(tt(lang, "Partner logo updated", "合伙人 Logo 已更新"));
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        e.target.value = "";
    };
    const Toggle = ({ on, onClick, testid }) => (
        <button onClick={onClick} disabled={busy} data-testid={testid}
            className={`relative w-10 h-[22px] rounded-full transition-colors shrink-0 ${on ? "bg-cafe-olive" : "bg-cafe-espresso/20"}`}>
            <span className={`absolute top-[3px] h-4 w-4 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-[3px]"}`} />
        </button>
    );
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 p-4 sm:p-5 mb-4" data-testid="sb-doc-settings">
            <p className="text-[12px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-3 flex items-center gap-2">⚙️ {tt(lang, "Proposal settings", "提案设置")}
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-cafe-mocha" />}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 rounded-xl bg-white border border-cafe-espresso/10 p-3.5">
                    <Toggle on={ds.show_prices !== false} onClick={() => save({ show_prices: ds.show_prices === false })} testid="ds-toggle-prices" />
                    <div className="min-w-0">
                        <p className="text-[13px] font-medium text-cafe-espresso">{tt(lang, "Show pricing", "显示报价费用")}</p>
                        <p className="text-[11px] text-cafe-mocha">{tt(lang, "Off = proposal PDF & client link hide all RM amounts", "关闭后提案 PDF 与客户链接完全不出现价钱")}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-white border border-cafe-espresso/10 p-3.5">
                    <Toggle on={!!ds.show_partner_logo} onClick={() => save({ show_partner_logo: !ds.show_partner_logo })} testid="ds-toggle-partner" />
                    <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-cafe-espresso">{tt(lang, "Partner co-brand logo", "合伙人联名 Logo")}</p>
                        <p className="text-[11px] text-cafe-mocha">{tt(lang, "kaily. × partner on proposal header", "提案页眉显示 kaily. × 合伙人")}</p>
                    </div>
                    {partner?.logo_url && <img src={partner.logo_url} alt="partner" className="h-9 w-9 rounded-lg object-cover shrink-0" data-testid="ds-partner-logo-img" />}
                    <button onClick={() => fileRef.current?.click()} className="text-[11px] text-cafe-olive hover:underline shrink-0" data-testid="ds-upload-logo">
                        {partner?.logo_url ? tt(lang, "Replace", "更换") : tt(lang, "Upload", "上传")}</button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="ds-logo-input" />
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-white border border-cafe-espresso/10 p-3.5">
                    <Toggle on={ds.show_ai_system !== false} onClick={() => save({ show_ai_system: ds.show_ai_system === false })} testid="ds-toggle-ai" />
                    <div className="min-w-0">
                        <p className="text-[13px] font-medium text-cafe-espresso">{tt(lang, "Mention AI content system", "提及 AI 内容系统")}</p>
                        <p className="text-[11px] text-cafe-mocha">{tt(lang, "Off = AI rewrites proposal as planned organic IG posting (first switch takes ~20s)", "关闭后 AI 重写提案为「有规划持续发内容保持账号健康成长」（首次约 20 秒）")}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl bg-white border border-cafe-espresso/10 p-3.5">
                    <Toggle on={!!ds.include_menu_shoot} onClick={() => save({ include_menu_shoot: !ds.include_menu_shoot })} testid="ds-toggle-menu-shoot" />
                    <div className="min-w-0">
                        <p className="text-[13px] font-medium text-cafe-espresso">{tt(lang, "Product / menu shoot suggestion", "产品 / 菜单拍摄建议")}</p>
                        <p className="text-[11px] text-cafe-mocha">{tt(lang, "On = AI adds a tailored product & menu shoot section to the proposal (first switch takes ~15s)", "开启后 AI 为这个商家生成产品/菜单拍摄建议段落（首次约 15 秒）")}</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- 2-page proposal preview ----------
function ProposalPreview({ p, onShare }) {
    const lang = useLang();
    const p1 = p.page1 || {}, p2 = p.page2 || {};
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 p-5 mb-4" data-testid="sb-proposal-preview">
            <p className="text-[12px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-3 flex items-center gap-1.5">
                <FileText className="h-4 w-4" /> {tt(lang, "Proposal — max 2 pages", "提案 — 最多 2 页")}</p>
            <p className="text-[13.5px] text-cafe-espresso/90 mb-4 border-l-2 border-cafe-olive pl-3">{cx(lang, p1, "exec_summary")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-[13px] mb-4">
                <div><p className="text-[11px] uppercase tracking-wider text-red-800 mb-2">{tt(lang, "3 challenges", "3 大挑战")}</p>
                    <ul className="space-y-1.5">{(p1.challenges || []).map((c, i) => <li key={i}>✗ {li(lang, c)}</li>)}</ul></div>
                <div><p className="text-[11px] uppercase tracking-wider text-green-800 mb-2">{tt(lang, "3 solutions", "3 大解决方案")}</p>
                    <ul className="space-y-1.5">{(p2 && p1.solutions ? p1.solutions : []).map((s, i) => <li key={i}>✓ {li(lang, s)}</li>)}</ul></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-[12.5px]">
                <div><p className="text-[11px] uppercase tracking-wider text-cafe-mocha mb-2">{tt(lang, "Timeline", "时间线")}</p>
                    <ul className="space-y-1">{(p2.timeline || []).map((w, i) => <li key={i}><b>W{w.week}</b> {li(lang, { en: w.title_en, cn: w.title_cn })}</li>)}</ul></div>
                <div><p className="text-[11px] uppercase tracking-wider text-cafe-mocha mb-2">{tt(lang, "Deliverables", "交付物")}</p>
                    <ul className="space-y-1">{(p2.deliverables || []).map((d, i) => <li key={i}>· {li(lang, d)}</li>)}</ul></div>
                <div><p className="text-[11px] uppercase tracking-wider text-cafe-mocha mb-2">{tt(lang, "Expected results", "预期成果")}</p>
                    <ul className="space-y-1">{(p2.expected_results || []).map((r, i) => <li key={i}>★ {li(lang, r)}</li>)}</ul></div>
            </div>
            <Button onClick={onShare} variant="outline" className="mt-4 rounded-full border-cafe-espresso/20 h-8 px-4 text-[12px]" data-testid="sb-copy-proposal-link">
                <Share2 className="mr-1.5 h-3 w-3" /> {tt(lang, "Copy client proposal link", "复制客户提案链接")}</Button>
        </section>
    );
}

// ---------- editable quotation ----------
function QuoteEditor({ leadId }) {
    const lang = useLang();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = async () => {
        const { data } = await axios.get(`${API}/sales-brain/leads/${leadId}/quote`, { headers: studioHeaders() });
        setQ(data.quote);
    };
    useEffect(() => { if (open && !q) load(); }, [open]); // eslint-disable-line
    const setItem = (i, patch) => setQ((p) => ({ ...p, items: p.items.map((it, j) => j === i ? { ...it, ...patch } : it) }));
    const subtotal = (q?.items || []).filter((it) => it.enabled !== false)
        .reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
    const total = Math.max(subtotal - (Number(q?.discount) || 0), 0);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await axios.put(`${API}/sales-brain/leads/${leadId}/quote`, q, { headers: studioHeaders() });
            setQ(data.quote);
            toast.success(tt(lang, "Quote saved — synced everywhere", "报价已保存 — 提案/PDF 全部同步"));
        } catch { toast.error(tt(lang, "Save failed", "保存失败")); }
        setBusy(false);
    };
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 mb-4 overflow-hidden" data-testid="sb-quote-editor">
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 p-4 text-left text-[13px] font-medium text-cafe-espresso" data-testid="quote-editor-toggle">
                💰 {tt(lang, "Edit quotation (items · discount · auto totals)", "编辑报价单（项目 · 折扣 · 自动算总额）")}
                <ChevronDown className={`ml-auto h-4 w-4 text-cafe-mocha transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (q ? (
                <div className="px-4 pb-4">
                    <div className="space-y-2">
                        {(q.items || []).map((it, i) => (
                            <div key={i} className={`flex flex-wrap items-center gap-2 rounded-lg bg-white p-2.5 ${it.enabled === false ? "opacity-45" : ""}`} data-testid={`quote-item-${i}`}>
                                <input type="checkbox" checked={it.enabled !== false} onChange={(e) => setItem(i, { enabled: e.target.checked })}
                                    className="h-4 w-4 accent-[#5B6B3C]" data-testid={`quote-item-check-${i}`} />
                                <input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })}
                                    className="flex-1 min-w-[160px] rounded-md border border-cafe-espresso/12 px-2.5 py-1.5 text-[12.5px] outline-none focus:border-cafe-olive" />
                                <input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} min="0"
                                    className="w-16 rounded-md border border-cafe-espresso/12 px-2 py-1.5 text-[12.5px] text-right outline-none" />
                                <div className="flex items-center gap-1 text-[12px] text-cafe-mocha">RM
                                    <input type="number" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} min="0"
                                        className="w-24 rounded-md border border-cafe-espresso/12 px-2 py-1.5 text-[12.5px] text-right outline-none" />
                                </div>
                                <Trash2 onClick={() => setQ((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))}
                                    className="h-4 w-4 text-cafe-mocha/50 hover:text-red-700 cursor-pointer" />
                            </div>))}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                        <button onClick={() => setQ((p) => ({ ...p, items: [...p.items, { name: "", description: "", qty: 1, unit_price: 0, enabled: true }] }))}
                            className="text-[12px] text-cafe-olive flex items-center gap-1 hover:underline" data-testid="quote-add-item">
                            <Plus className="h-3.5 w-3.5" /> {tt(lang, "Add item", "添加项目")}</button>
                        <div className="ml-auto flex items-center gap-2 text-[12.5px]">
                            {tt(lang, "Discount RM", "折扣 RM")}
                            <input type="number" value={q.discount || 0} min="0" onChange={(e) => setQ((p) => ({ ...p, discount: e.target.value }))}
                                className="w-24 rounded-md border border-cafe-espresso/12 px-2 py-1.5 text-right outline-none" data-testid="quote-discount" />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-cafe-espresso/10">
                        <p className="text-[13px]">{tt(lang, "Total", "总计")} <b className="text-cafe-espresso text-[16px]" data-testid="quote-grand-total">RM {total.toLocaleString()}</b>
                            <span className="text-cafe-mocha ml-3">{tt(lang, "Deposit", "订金")} {q.deposit_pct || 50}% = RM {(total * (q.deposit_pct || 50) / 100).toLocaleString()}</span></p>
                        <Button onClick={save} disabled={busy} data-testid="quote-save"
                            className="ml-auto rounded-full bg-cafe-olive text-cafe-cream h-8 px-5 text-[12px]">
                            {busy ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <Save className="mr-1.5 h-3 w-3" />} {tt(lang, "Save", "保存报价")}
                        </Button>
                    </div>
                </div>
            ) : <p className="text-[12px] text-cafe-mocha px-4 pb-4">…</p>)}
        </section>
    );
}

// ---------- objections quick answers ----------
function Objections({ obs }) {
    const lang = useLang();
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 p-5 mb-4" data-testid="sb-objections">
            <p className="text-[12px] uppercase tracking-[0.2em] text-cafe-olive font-semibold mb-3">🛡 {tt(lang, "Objection quick answers", "异议秒回")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {obs.map((o, i) => (
                    <div key={i} className="rounded-xl bg-white border border-cafe-espresso/10 p-3.5 text-[12.5px]" data-testid={`sb-objection-${i}`}>
                        <p className="text-red-800 mb-1.5">"{li(lang, { en: o.q_en, cn: o.q_cn })}"</p>
                        <p className="text-cafe-espresso/90">→ {li(lang, { en: o.a_en, cn: o.a_cn })}</p>
                        <button onClick={() => copyText(lang === "en" ? o.a_en : o.a_cn || o.a_en)}
                            className="mt-2 text-[11px] text-cafe-olive flex items-center gap-1 hover:underline"><Copy className="h-3 w-3" /> {tt(lang, "Copy", "复制")}</button>
                    </div>))}
            </div>
        </section>
    );
}

// ---------- collapse ----------
function Collapse({ icon: Icon, title, sub, children, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <section className="rounded-2xl border border-cafe-espresso/12 bg-white/60 mb-3 overflow-hidden" data-testid={testid}>
            <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/60 transition-colors" data-testid={`${testid}-toggle`}>
                <Icon className="h-4 w-4 text-cafe-olive shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                    <p className="text-[13px] font-medium text-cafe-espresso">{title}</p>
                    {sub && !open && <p className="text-[11px] text-cafe-mocha truncate">{sub}</p>}
                </div>
                <ChevronDown className={`ml-auto h-4 w-4 text-cafe-mocha transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <div className="px-5 pb-5">{children}</div>}
        </section>
    );
}
